import React, { useMemo, useState } from 'react';
import { isMobileOnly, isTablet } from 'mobile-device-detect';

import { createContext } from '../createContext';
import { withWindow } from '../withWindow';

export type MediaScreen = 'mobile' | 'tablet' | 'desktop';

interface DefaultMediaMatchContext {
  defaultScreen: MediaScreen | undefined;
}

const [Provider, useDefaultMediaMatchContext] = createContext<DefaultMediaMatchContext>({ name: 'DefaultMediaMatch', strict: false });

export const DefaultMediaMatchProvider: React.FC<Partial<Pick<DefaultMediaMatchContext, 'defaultScreen'>>> = ({ children, defaultScreen }) => {
  const mobile = useMemo(() => {
    if (isMobileOnly) {
      return 'mobile';
    }

    return withWindow<'mobile' | false>(global => {
      const windowWidth = global.innerWidth;
      return windowWidth <= 1024 ? 'mobile' : false;
    }, false);
  }, []);

  const tablet = useMemo(() => {
    if (isTablet) {
      return 'tablet';
    }

    return withWindow<'tablet' | false>(global => {
      const windowWidth = global.innerWidth;

      return windowWidth > 1024 && windowWidth < 1280 ? 'tablet' : false;
    }, false);
  }, []);

  // Stable screen value: SSR-provided prop (guest site) or client detection (admin/CSR).
  // Not updated after mount — downstream hooks (useMediaQuery, useResponsive) self-correct
  // to real matchMedia values via their own useEffect. This also preserves explicit
  // overrides from nested providers (e.g., admin components forcing defaultScreen="desktop").
  const [screen] = useState<MediaScreen | undefined>(() => defaultScreen || mobile || tablet || 'desktop');

  const ctx = useMemo(() => ({ defaultScreen: screen }), [screen]);
  return <Provider value={ctx}>{children}</Provider>;
};

DefaultMediaMatchProvider.displayName = 'DefaultMediaMatchProvider';

export { useDefaultMediaMatchContext };
